import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import { useDialog } from './ui/DialogProvider';
import { formatCurrencyRate } from '../utils/currencyUtils';
import { Plus, Edit3, Trash2, Tag, DollarSign, MapPin, ChevronRight, Hash, Coins } from 'lucide-react';

// Category Widget Component
const CategoryWidget = () => {
  const { confirm, notify } = useDialog();
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedParent, setExpandedParent] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/categories');
      setCategories(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching categories');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchCategories();
    setEditingCategory(null);
    setShowForm(false);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (await confirm('Вы уверены, что хотите удалить эту категорию?', { title: 'Удалить категорию?' })) {
      try {
        await apiClient.delete(`/categories/${id}`);
        await fetchCategories();
      } catch (err) {
        await notify(err.response?.data?.message || 'Не удалось удалить категорию', { title: 'Ошибка удаления' });
        console.error('Error deleting category:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-4 text-gray-700 dark:text-slate-200">
          Загрузка категорий...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-4 text-accent">
          {error}
        </div>
      </Card>
    );
  }

  // Group categories by parent_id to show hierarchy
  const topLevelCategories = categories.filter(cat => !cat.parent_id);
  const childCategories = categories.filter(cat => cat.parent_id);

  const getChildCategories = (parentId) => {
    return childCategories.filter(cat => cat.parent_id === parentId);
  };

  const toggleExpand = (categoryId) => {
    setExpandedParent(expandedParent === categoryId ? null : categoryId);
  };

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Категории расходов</h3>
        <Button 
          onClick={handleAddNew}
          icon={Plus}
          size="sm"
        >
          Добавить
        </Button>
      </div>

      {showForm && (
        <CategoryForm
          category={editingCategory}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingCategory(null);
          }}
          allCategories={categories}
        />
      )}

      <div className="space-y-2">
        {topLevelCategories.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm dark:text-slate-400">
            Нет категорий расходов
          </div>
        ) : (
          topLevelCategories.map(category => {
            const children = getChildCategories(category.id);
            const isExpanded = expandedParent === category.id;
            
            return (
              <div key={category.id}>
                <div className="flex justify-between items-center p-3 rounded-lg bg-white/60 dark:bg-white/10">
                  <div className="flex items-center">
                    <Tag size={16} className="mr-2 text-accent" />
                    <span className="font-medium text-gray-800 dark:text-slate-100">{category.name}</span>
                    {children.length > 0 && (
                      <button 
                        onClick={() => toggleExpand(category.id)}
                        className="ml-2 text-gray-500 hover:text-gray-700 dark:text-slate-300 dark:hover:text-slate-100"
                      >
                        <ChevronRight 
                          size={16} 
                          className={`transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </button>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-1 rounded bg-white/60 hover:bg-white/80 dark:bg-white/10 dark:hover:bg-white/20"
                      title="Редактировать"
                    >
                      <Edit3 size={14} className="text-gray-700 dark:text-slate-100" />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="p-1 rounded bg-base hover:bg-base dark:bg-base dark:hover:bg-base"
                      title="Удалить"
                    >
                      <Trash2 size={14} className="text-accent" />
                    </button>
                  </div>
                </div>
                
                {isExpanded && children.length > 0 && (
                  <div className="ml-6 mt-2 space-y-2">
                    {children.map(child => (
                      <div key={child.id} className="flex justify-between items-center p-3 rounded-lg ml-4 bg-white/50 dark:bg-white/5">
                        <div className="flex items-center">
                          <Tag size={14} className="mr-2 text-accent" />
                          <span className="font-medium text-sm text-gray-800 dark:text-slate-100">{child.name}</span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(child)}
                            className="p-1 rounded bg-white/60 hover:bg-white/80 dark:bg-white/10 dark:hover:bg-white/20"
                            title="Редактировать"
                          >
                            <Edit3 size={12} className="text-gray-700 dark:text-slate-100" />
                          </button>
                          <button
                            onClick={() => handleDelete(child.id)}
                            className="p-1 rounded bg-base hover:bg-base dark:bg-base dark:hover:bg-base"
                            title="Удалить"
                          >
                            <Trash2 size={12} className="text-accent" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};

// Category Form Component
const CategoryForm = ({ category, onSave, onCancel, allCategories }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    type: 'expense', // Always expense for categories
    parent_id: category?.parent_id || null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (category && category.id) {
        // Update existing category
        await apiClient.put(`/categories/${category.id}`, formData);
      } else {
        // Create new category
        await apiClient.post('/categories', formData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
      console.error('Error saving category:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter to show only parent categories as options for parent_id
  const parentCategories = allCategories.filter(cat => !cat.parent_id);

  return (
    <div className="mb-4 p-4 rounded-lg bg-white/60 dark:bg-white/10">
      <h4 className="text-md font-medium text-gray-800 mb-3 dark:text-slate-100">
        {category?.id ? 'Редактировать категорию' : 'Добавить категорию'}
      </h4>
      {error && (
        <div className="mb-3 p-2 bg-base text-accent rounded text-sm">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Название *"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Введите название категории"
        />

        <Input
          label="Описание"
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Введите описание категории"
        />

        <Select label="Родительская категория" name="parent_id" value={formData.parent_id || ''} onChange={handleChange}>
            <option value="">Без родителя (основная категория)</option>
            {parentCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
        </Select>

        <div className="flex justify-end space-x-2 pt-2">
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={onCancel}
          >
            Отмена
          </Button>
          <Button 
            type="submit" 
            variant={category ? "secondary" : "primary"}
            size="sm"
            loading={loading}
          >
            {category ? 'Сохранить' : 'Добавить'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Income Source Widget Component
const IncomeSourceWidget = () => {
  const { confirm, notify } = useDialog();
  const [sources, setSources] = useState([]);
  const [editingSource, setEditingSource] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/income-sources');
      setSources(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching income sources');
      console.error('Error fetching income sources:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchSources();
    setEditingSource(null);
    setShowForm(false);
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (await confirm('Вы уверены, что хотите удалить этот источник дохода?', { title: 'Удалить источник дохода?' })) {
      try {
        await apiClient.delete(`/income-sources/${id}`);
        await fetchSources();
      } catch (err) {
        await notify(err.response?.data?.message || 'Не удалось удалить источник дохода', { title: 'Ошибка удаления' });
        console.error('Error deleting income source:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingSource(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-4 text-gray-700 dark:text-slate-200">
          Загрузка источников дохода...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-4 text-accent">
          {error}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Источники дохода</h3>
        <Button 
          onClick={handleAddNew}
          icon={Plus}
          size="sm"
        >
          Добавить
        </Button>
      </div>

      {showForm && (
        <IncomeSourceForm
          source={editingSource}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingSource(null);
          }}
        />
      )}

      <div className="space-y-2">
        {sources.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm dark:text-slate-400">
            Нет источников дохода
          </div>
        ) : (
          sources.map(source => (
            <div key={source.id} className="flex justify-between items-center p-3 rounded-lg bg-white/60 dark:bg-white/10">
              <div className="flex items-center">
                <DollarSign size={16} className="mr-2 text-accent" />
                <span className="font-medium text-gray-800 dark:text-slate-100">{source.name}</span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(source)}
                  className="p-1 rounded bg-white/60 hover:bg-white/80 dark:bg-white/10 dark:hover:bg-white/20"
                  title="Редактировать"
                >
                  <Edit3 size={14} className="text-gray-700 dark:text-slate-100" />
                </button>
                <button
                  onClick={() => handleDelete(source.id)}
                  className="p-1 rounded bg-base hover:bg-base dark:bg-base dark:hover:bg-base"
                  title="Удалить"
                >
                  <Trash2 size={14} className="text-accent" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Income Source Form Component
const IncomeSourceForm = ({ source, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: source?.name || '',
    description: source?.description || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (source && source.id) {
        // Update existing income source
        await apiClient.put(`/income-sources/${source.id}`, formData);
      } else {
        // Create new income source
        await apiClient.post('/income-sources', formData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
      console.error('Error saving income source:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-4 p-4 rounded-lg bg-white/60 dark:bg-white/10">
      <h4 className="text-md font-medium text-gray-800 mb-3 dark:text-slate-100">
        {source?.id ? 'Редактировать источник' : 'Добавить источник'}
      </h4>
      {error && (
        <div className="mb-3 p-2 bg-base text-accent rounded text-sm">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Название *"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Введите название источника"
        />

        <Input
          label="Описание"
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Введите описание источника"
        />

        <div className="flex justify-end space-x-2 pt-2">
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={onCancel}
          >
            Отмена
          </Button>
          <Button 
            type="submit" 
            variant={source ? "secondary" : "primary"}
            size="sm"
            loading={loading}
          >
            {source ? 'Сохранить' : 'Добавить'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Location Widget Component
const LocationWidget = () => {
  const { confirm, notify } = useDialog();
  const [locations, setLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/locations');
      setLocations(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching locations');
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchLocations();
    setEditingLocation(null);
    setShowForm(false);
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (await confirm('Вы уверены, что хотите удалить это место хранения?', { title: 'Удалить место хранения?' })) {
      try {
        await apiClient.delete(`/locations/${id}`);
        await fetchLocations();
      } catch (err) {
        await notify(err.response?.data?.message || 'Не удалось удалить место хранения', { title: 'Ошибка удаления' });
        console.error('Error deleting location:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingLocation(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-4 text-gray-700 dark:text-slate-200">
          Загрузка мест хранения...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-4 text-accent">
          {error}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Места хранения</h3>
        <Button 
          onClick={handleAddNew}
          icon={Plus}
          size="sm"
        >
          Добавить
        </Button>
      </div>

      {showForm && (
        <LocationForm
          location={editingLocation}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingLocation(null);
          }}
        />
      )}

      <div className="space-y-2">
        {locations.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm dark:text-slate-400">
            Нет мест хранения
          </div>
        ) : (
          locations.map(location => (
            <div key={location.id} className="flex justify-between items-center p-3 rounded-lg bg-white/60 dark:bg-white/10">
              <div className="flex items-center">
                <MapPin size={16} className="mr-2 text-accent" />
                <div>
                  <div className="font-medium text-gray-800 dark:text-slate-100">{location.name}</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">{location.currency}</div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(location)}
                  className="p-1 rounded bg-white/60 hover:bg-white/80 dark:bg-white/10 dark:hover:bg-white/20"
                  title="Редактировать"
                >
                  <Edit3 size={14} className="text-gray-700 dark:text-slate-100" />
                </button>
                <button
                  onClick={() => handleDelete(location.id)}
                  className="p-1 rounded bg-base hover:bg-base dark:bg-base dark:hover:bg-base"
                  title="Удалить"
                >
                  <Trash2 size={14} className="text-accent" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Location Form Component
const LocationForm = ({ location, onSave, onCancel }) => {
  const [currencies, setCurrencies] = useState([]);
  const [formData, setFormData] = useState({
    name: location?.name || '',
    description: location?.description || '',
    currency: location?.currency || 'RUB',
    initial_balance: location?.initial_balance || 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/currencies')
      .then((response) => setCurrencies(response.data.data || []))
      .catch(() => setError('Не удалось загрузить справочник валют'));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (location && location.id) {
        // Update existing location
        await apiClient.put(`/locations/${location.id}`, formData);
      } else {
        // Create new location
        await apiClient.post('/locations', formData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
      console.error('Error saving location:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-4 p-4 rounded-lg bg-white/60 dark:bg-white/10">
      <h4 className="text-md font-medium text-gray-800 mb-3 dark:text-slate-100">
        {location?.id ? 'Редактировать место' : 'Добавить место'}
      </h4>
      {error && (
        <div className="mb-3 p-2 bg-base text-accent rounded text-sm">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Название *"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Введите название места"
        />

        <Select
          label="Валюта"
          name="currency"
          value={formData.currency}
          onChange={handleChange}
        >
          {currencies.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {currency.code} — {currency.name}
            </option>
          ))}
        </Select>

        <Input
          label="Описание"
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Введите описание места"
        />

        <Input
          label="Начальный баланс"
          type="number"
          name="initial_balance"
          value={formData.initial_balance}
          onChange={handleChange}
          step="0.01"
          placeholder="0.00"
          className="hide-number-controls"
        />

        <div className="flex justify-end space-x-2 pt-2">
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={onCancel}
          >
            Отмена
          </Button>
          <Button 
            type="submit" 
            variant={location ? "secondary" : "primary"}
            size="sm"
            loading={loading}
          >
            {location ? 'Сохранить' : 'Добавить'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const CurrencyWidget = () => {
  const { confirm, notify } = useDialog();
  const [currencies, setCurrencies] = useState([]);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/currencies');
      setCurrencies(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось загрузить валюты');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCurrencies(); }, []);

  const handleDelete = async (code) => {
    if (!await confirm(`Удалить валюту ${code}?`, { title: 'Удалить валюту?' })) return;
    try {
      await apiClient.delete(`/currencies/${code}`);
      await fetchCurrencies();
    } catch (err) {
      await notify(err.response?.data?.message || 'Не удалось удалить валюту', { title: 'Ошибка удаления' });
    }
  };

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Валюты</h3>
          <p className="text-xs text-muted mt-1">Курс ЦБ: стоимость 1 единицы валюты в рублях.</p>
        </div>
        <Button onClick={() => { setEditingCurrency(null); setShowForm(true); }} icon={Plus} size="sm">Добавить</Button>
      </div>
      {error && <div className="mb-3 text-sm text-accent">{error}</div>}
      {showForm && <CurrencyForm currency={editingCurrency} onCancel={() => { setShowForm(false); setEditingCurrency(null); }} onSave={async () => { await fetchCurrencies(); setShowForm(false); setEditingCurrency(null); }} />}
      {loading ? <div className="text-center py-4 text-muted">Загрузка валют...</div> : (
        <div className="space-y-2">
          {currencies.map((currency) => (
            <div key={currency.code} className="flex justify-between items-center p-3 rounded-lg bg-white/60 dark:bg-white/10">
              <div className="flex items-center gap-2">
                <Coins size={16} className="text-accent" />
                <div>
                  <div className="font-medium text-gray-800 dark:text-slate-100">{currency.code} — {currency.name}</div>
                  {currency.code !== 'RUB' && <div className="text-xs text-muted">Курс ЦБ: {formatCurrencyRate(currency.rate_to_rub)}</div>}
                </div>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => { setEditingCurrency(currency); setShowForm(true); }} className="p-1 rounded bg-white/60 hover:bg-white/80 dark:bg-white/10" title="Редактировать"><Edit3 size={14} /></button>
                {currency.code !== 'RUB' && <button onClick={() => handleDelete(currency.code)} className="p-1 rounded bg-base hover:bg-base" title="Удалить"><Trash2 size={14} className="text-accent" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const CurrencyForm = ({ currency, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    code: currency?.code || '', name: currency?.name || '', symbol: currency?.symbol || '',
    rate_to_rub: currency?.rate_to_rub ?? ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      if (currency) await apiClient.put(`/currencies/${currency.code}`, formData);
      else await apiClient.post('/currencies', formData);
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось сохранить валюту');
    } finally { setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="mb-4 p-4 rounded-lg bg-white/60 dark:bg-white/10 space-y-3">
      {error && <div className="text-sm text-accent">{error}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Код *" name="code" value={formData.code} onChange={handleChange} required maxLength="3" disabled={Boolean(currency)} placeholder="USD" className="uppercase" />
        <Input label="Название *" name="name" value={formData.name} onChange={handleChange} required placeholder="Доллар США" />
        <Input label="Символ" name="symbol" value={formData.symbol} onChange={handleChange} placeholder="$" />
        <Input label="Курс ЦБ РФ, ₽ *" type="number" name="rate_to_rub" value={formData.rate_to_rub} onChange={handleChange} required min="0.01" step="0.01" className="hide-number-controls" disabled={currency?.code === 'RUB'} />
      </div>
      <div className="flex justify-end space-x-2"><Button type="button" variant="ghost" size="sm" onClick={onCancel}>Отмена</Button><Button type="submit" size="sm" loading={loading}>{currency ? 'Сохранить' : 'Добавить'}</Button></div>
    </form>
  );
};

// Tag Widget Component
const TagWidget = () => {
  const { confirm, notify } = useDialog();
  const [tags, setTags] = useState([]);
  const [editingTag, setEditingTag] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/tags');
      setTags(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching tags');
      console.error('Error fetching tags:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchTags();
    setEditingTag(null);
    setShowForm(false);
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (await confirm('Вы уверены, что хотите удалить этот тег?', { title: 'Удалить тег?' })) {
      try {
        await apiClient.delete(`/tags/${id}`);
        await fetchTags();
      } catch (err) {
        await notify(err.response?.data?.message || 'Не удалось удалить тег', { title: 'Ошибка удаления' });
        console.error('Error deleting tag:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingTag(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-4 text-gray-700 dark:text-slate-200">
          Загрузка тегов...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-4 text-accent">
          {error}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Теги</h3>
        <Button
          onClick={handleAddNew}
          icon={Plus}
          size="sm"
        >
          Добавить
        </Button>
      </div>

      {showForm && (
        <TagForm
          tag={editingTag}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingTag(null);
          }}
        />
      )}

      <div className="space-y-2">
        {tags.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm dark:text-slate-400">
            Нет тегов
          </div>
        ) : (
          tags.map(tag => (
            <div key={tag.id} className="flex justify-between items-center p-3 rounded-lg bg-white/60 dark:bg-white/10">
              <div className="flex items-center">
                <Hash size={16} className="mr-2 text-accent" />
                <span className="font-medium text-gray-800 dark:text-slate-100">{tag.name}</span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(tag)}
                  className="p-2 rounded-lg bg-white/60 hover:bg-white/80 dark:bg-white/10 dark:hover:bg-white/20"
                  title="Редактировать"
                >
                  <Edit3 size={16} className="text-gray-700 dark:text-slate-100" />
                </button>
                <button
                  onClick={() => handleDelete(tag.id)}
                  className="p-2 rounded-lg bg-base hover:bg-base"
                  title="Удалить"
                >
                  <Trash2 size={16} className="text-accent" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Tag Form Component
const TagForm = ({ tag, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: tag?.name || '',
    description: tag?.description || '',
    color: tag?.color || '#8884d8' // Default purple color
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (tag && tag.id) {
        // Update existing tag
        await apiClient.put(`/tags/${tag.id}`, formData);
      } else {
        // Create new tag
        await apiClient.post('/tags', formData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
      console.error('Error saving tag:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <h3 className="text-xl font-bold text-gray-800 dark:text-slate-100 mb-4">
        {tag?.id ? 'Редактировать тег' : 'Добавить новый тег'}
      </h3>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-base text-accent dark:bg-base dark:text-accent">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Название *"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Введите название тега"
        />

        <Input
          label="Описание"
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Введите описание тега"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Цвет</label>
          <input
            type="color"
            name="color"
            value={formData.color}
            onChange={handleChange}
            className="w-full h-12 rounded-xl border border-white/40 bg-white/60 text-gray-800 shadow-sm backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent cursor-pointer dark:border-white/10 dark:bg-slate-900/60 dark:text-slate-100"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
          >
            Отмена
          </Button>
          <Button
            type="submit"
            variant={tag ? "secondary" : "primary"}
            loading={loading}
          >
            {tag ? 'Сохранить' : 'Добавить'}
          </Button>
        </div>
      </form>
    </Card>
  );
};


// Main References Component
const References = () => {
  const [activeTab, setActiveTab] = useState('categories');

  const tabs = [
    { key: 'categories', label: 'Категории расходов', icon: Tag },
    { key: 'income',     label: 'Источники дохода',   icon: DollarSign },
    { key: 'locations',  label: 'Места хранения',     icon: MapPin },
    { key: 'currencies', label: 'Валюты',             icon: Coins },
    { key: 'tags',       label: 'Теги',               icon: Hash },
  ];

  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-4xl font-semibold text-ink">Справочники</h2>
      </section>

      <section className="max-w-[52rem]">
        <div
          className="[&::-webkit-scrollbar]:hidden"
          style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line-0)', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {tabs.map(({ key, label, icon: Icon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '10px 16px',
                  fontSize: 16,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'var(--accent-0)' : 'var(--ink-2)',
                  background: 'none',
                  border: 'none',
                  borderBottom: isActive ? '2px solid var(--accent-0)' : '2px solid transparent',
                  marginBottom: -1,
                  cursor: 'pointer',
                  transition: 'color 0.15s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>

        <div className="pt-6 max-w-[52rem]">
          {activeTab === 'categories' && <CategoryWidget />}
          {activeTab === 'income'     && <IncomeSourceWidget />}
          {activeTab === 'locations'  && <LocationWidget />}
          {activeTab === 'currencies' && <CurrencyWidget />}
          {activeTab === 'tags'       && <TagWidget />}
        </div>
      </section>
    </div>
  );
};

export default References;
